import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IosShareIcon from "@mui/icons-material/IosShare";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled, ThemeProvider } from "@mui/material/styles";

import type { SessionRecapResponse, SessionRecapStats } from "@volley/data";

import logger from "../../log";
import { darkTheme } from "../../theme";
import userMonitor from "../../userMonitor";
import fetchApi, {
    FetchError,
    fetchApiBlob,
    logFetchError,
} from "../../util/fetchApi";
import {
    canShareCustomPhoto,
    canShareRecap,
    downloadCard,
    shareRecap,
    type SharePayload,
} from "../../util/nativeShare";
import PageTitle from "../common/PageTitle";

import { CARD_SIZE } from "./cardChoice";
import { loadPhoto } from "./customPhoto";
import { useCustomCard } from "./useCustomCard";

type Source = "end_flow" | "history";

/** How long the drag hint stays on a fresh photo before fading. */
const DRAG_HINT_MS = 2500;

/**
 * The app closes a session without waiting, so a recap opened straight from
 * the end-of-session flow can arrive before the server knows it ended.
 */
const NOT_ENDED_RETRIES = 4;
const NOT_ENDED_RETRY_MS = 1500;

const numberFormat = new Intl.NumberFormat("en-US");
const TITLE = ["Session Recap"];

function deviceTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
}

/** Volley's account, for players to tag when they post. */
const VOLLEY_HANDLE = "@trainwithvolley";

function shareText(stats: SessionRecapStats): string {
    const balls = numberFormat.format(stats.ballsHit);
    const sport = stats.sport ? ` ${stats.sport.toLowerCase()}` : "";
    return `Just hit ${balls}${sport} balls on Volley 🎾 ${VOLLEY_HANDLE}`;
}

/**
 * The card carries no name: marketing's design leaves it off.
 */
const SHOW_NAME = false;
/**
 * The club is always on the card (home courts never are; the server leaves
 * them off). Hiding it can come back as a toggle if players ask for it.
 */
const SHOW_LOCATION = true;

/** A canvas that takes `sx`; Box would read width and height as styles. */
const Canvas = styled("canvas")({});

/**
 * What the card is previewed on without a photo: a plain stage standing in
 * for wherever it's posted, edged with a dashed line to show the card itself
 * has no background.
 */
const TRANSPARENT_STAGE = {
    borderRadius: 3,
    background: "linear-gradient(180deg, #2B3550 0%, #151C2E 100%)",
    outline: "1.5px dashed rgba(255, 255, 255, 0.3)",
    outlineOffset: "-6px",
} as const;

/** A note pinned to the foot of the card's preview; never part of the card. */
const CARD_NOTE_SX = {
    position: "absolute",
    bottom: "4%",
    left: "50%",
    transform: "translateX(-50%)",
    px: 1.5,
    py: 0.5,
    borderRadius: 99,
    bgcolor: "rgba(11, 23, 48, 0.72)",
    color: "common.white",
    fontWeight: 600,
    whiteSpace: "nowrap",
    pointerEvents: "none",
} as const;

/**
 * The card's own box inside the preview, so the notes above sit on the card
 * rather than somewhere in the space around it. Sized from the preview with
 * container units; a browser without them (iOS before 16) gets the whole
 * preview as the frame, and the notes sit at its foot instead.
 */
function cardFrameSx() {
    const { width, height } = CARD_SIZE;
    return {
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        "@supports (width: 1cqw)": {
            width: `min(100cqw, 100cqh * ${width} / ${height})`,
            height: `min(100cqh, 100cqw * ${height} / ${width})`,
        },
    } as const;
}

/**
 * The stats with nothing behind them: shared as they are, or laid over the
 * player's own photo on the device.
 */
function previewUrl(sessionId: number): string {
    const params = new URLSearchParams({
        showName: String(SHOW_NAME),
        showLocation: String(SHOW_LOCATION),
        timeZone: deviceTimeZone(),
    });
    return `/api/session-recaps/sessions/${sessionId}/image?${params}`;
}

interface CardImage {
    blob: Blob;
    objectUrl: string;
    /** The request it answered: a blob from before a toggle flip is stale. */
    url: string;
}

/** The card the player would share, as both a blob (to share) and a URL. */
function useCardImage(url: string | null) {
    const [image, setImage] = React.useState<CardImage | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const [attempt, setAttempt] = React.useState(0);

    React.useEffect(() => {
        if (!url) return undefined;
        let objectUrl: string | null = null;
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        fetchApiBlob(url)
            .then((blob) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setImage({ blob, objectUrl, url });
            })
            .catch((e) => {
                if (cancelled) return;
                logFetchError(e, "Could not load session recap card");
                setFailed(true);
            })
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [url, attempt]);

    const retry = React.useCallback(() => setAttempt((n) => n + 1), []);
    /** The card on screen is the one these settings would share. */
    const ready = !loading && !failed && image !== null && image.url === url;
    return { image, loading, failed, ready, retry };
}

export default function SessionRecapPage(): React.JSX.Element {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const sessionId = Number(id);
    const [searchParams] = useSearchParams();
    const source: Source =
        searchParams.get("source") === "end_flow" ? "end_flow" : "history";

    const [recap, setRecap] = React.useState<SessionRecapResponse | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [photo, setPhoto] = React.useState<ImageBitmap | null>(null);
    const [decodingPhoto, setDecodingPhoto] = React.useState(false);
    const [dragHintSeen, setDragHintSeen] = React.useState(false);
    const photoInput = React.useRef<HTMLInputElement>(null);
    const offerCustomPhoto = React.useMemo(canShareCustomPhoto, []);
    // An old app build has nowhere to send the card; it isn't sent here, but
    // a link or a back button can still land on the page.
    const supported = React.useMemo(canShareRecap, []);
    // The player's photo goes under the server's transparent card.
    const isCustom = photo !== null;
    const card = CARD_SIZE;
    const [busy, setBusy] = React.useState(false);
    const [needsTap, setNeedsTap] = React.useState(false);
    const gestureRefusals = React.useRef(0);
    // Set when there's no share sheet to open: the player saves the card.
    const [saveInstead, setSaveInstead] = React.useState(false);
    const [toast, setToast] = React.useState<string | null>(null);

    const close = React.useCallback(() => {
        // Back to the history list they came from; the end of a session
        // carries on to home.
        if (source === "history" && window.history.length > 1) navigate(-1);
        else navigate("/");
    }, [navigate, source]);

    React.useEffect(() => {
        if (!supported) {
            userMonitor.addAction("session_recap_unsupported_app", { source });
            return undefined;
        }
        if (!sessionId) {
            setError("We couldn't find that session.");
            return undefined;
        }
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const load = (attempt: number) => {
            fetchApi<SessionRecapResponse>(
                `/api/session-recaps/sessions/${sessionId}`,
            )
                .then((res) => {
                    if (cancelled) return;
                    if (
                        !res.eligible &&
                        res.reason === "not_ended" &&
                        source === "end_flow" &&
                        attempt < NOT_ENDED_RETRIES
                    ) {
                        timer = setTimeout(
                            () => load(attempt + 1),
                            NOT_ENDED_RETRY_MS,
                        );
                        return;
                    }
                    if (!res.eligible && source === "end_flow") {
                        // Nothing worth celebrating; carry on as before.
                        userMonitor.addAction("session_recap_skipped", {
                            reason: res.reason,
                            attempts: attempt + 1,
                        });
                        navigate("/", { replace: true });
                        return;
                    }
                    setRecap(res);
                    if (res.eligible) {
                        userMonitor.addAction("session_recap_viewed", {
                            source,
                        });
                    }
                })
                .catch((e) => {
                    if (cancelled) return;
                    logFetchError(e, "Could not load session recap");
                    if (source === "end_flow") {
                        userMonitor.addAction("session_recap_skipped", {
                            reason: "error",
                            status:
                                e instanceof FetchError ? e.statusCode : null,
                            attempts: attempt + 1,
                        });
                        navigate("/", { replace: true });
                    } else {
                        setError("We couldn't load this recap.");
                    }
                });
        };
        load(0);
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [sessionId, source, navigate, supported]);

    const eligible = recap?.eligible ? recap : null;
    const {
        image,
        loading: imageLoading,
        failed: imageFailed,
        ready: cardReady,
        retry: retryImage,
    } = useCardImage(eligible ? previewUrl(sessionId) : null);

    const custom = useCustomCard(
        photo,
        isCustom && cardReady && image ? image.blob : null,
        card,
    );
    const shareReady = isCustom ? custom.ready : cardReady;
    // The card as it's on screen now, which a change after a share attempt
    // (a photo added, removed or moved) makes the one to save.
    const currentCard = isCustom ? custom.blob : (image?.blob ?? null);
    const fileName = isCustom
        ? "volley-session-recap-your-photo.jpg"
        : "volley-session-recap.png";
    const previewFailed = isCustom ? custom.failed || imageFailed : imageFailed;
    const previewLoading =
        imageLoading || decodingPhoto || (isCustom && custom.loading);
    // Once, on a photo with something to bring in; gone at the first drag.
    const showDragHint =
        isCustom &&
        custom.movable &&
        !previewLoading &&
        !previewFailed &&
        !dragHintSeen;

    React.useEffect(() => {
        if (!showDragHint) return undefined;
        const timer = setTimeout(() => setDragHintSeen(true), DRAG_HINT_MS);
        return () => clearTimeout(timer);
    }, [showDragHint]);

    // Each photo is closed once it's replaced, or the page goes.
    React.useEffect(() => () => photo?.close(), [photo]);

    const pickPhoto = React.useCallback(() => {
        // Opened from the tap, so the browser lets the picker open.
        photoInput.current?.click();
    }, []);

    const onPhotoPicked = React.useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            // Picking the same photo again should still count as a change.
            e.target.value = "";
            // Cancelled: the previous card was never swapped out.
            if (!file) return;
            setDecodingPhoto(true);
            try {
                setPhoto(await loadPhoto(file));
                userMonitor.addAction("session_recap_photo_picked", {
                    type: file.type,
                });
            } catch (err) {
                logger.warn(`Could not open recap photo: ${String(err)}`);
                setToast("We couldn't open that photo. Try another one.");
            } finally {
                setDecodingPhoto(false);
            }
        },
        [],
    );

    // Back to the stats alone; the photo is closed by the effect above.
    const removePhoto = React.useCallback(() => setPhoto(null), []);

    const share = React.useCallback(async () => {
        if (!eligible || !shareReady || !currentCard) return;
        setBusy(true);
        setNeedsTap(false);
        const picked = { photo: isCustom };
        userMonitor.addAction("session_recap_share_tapped", picked);
        try {
            const payload: SharePayload = {
                image: currentCard,
                text: shareText(eligible.stats),
                fileName,
            };
            const outcome = await shareRecap(payload);
            userMonitor.addAction("session_recap_shared", {
                ...outcome,
                ...picked,
            });

            switch (outcome.status) {
                case "needs_gesture":
                    // Once is a spent tap; twice is a browser that won't.
                    gestureRefusals.current += 1;
                    if (gestureRefusals.current < 2) setNeedsTap(true);
                    else setSaveInstead(true);
                    return;
                case "no_share_sheet":
                case "failed":
                    setSaveInstead(true);
                    break;
                case "shared":
                case "handed_off":
                case "cancelled":
                    break;
            }
            gestureRefusals.current = 0;
        } catch (e) {
            logger.error(`Could not share session recap: ${String(e)}`);
            setToast("Sharing didn't work. Please try again.");
        } finally {
            setBusy(false);
        }
    }, [eligible, shareReady, isCustom, currentCard, fileName]);

    const content = (() => {
        if (!supported || error || (recap && !recap.eligible)) {
            const message = !supported
                ? "Update the Volley app to share your sessions."
                : (error ??
                  (recap && !recap.eligible && recap.reason === "not_ended"
                      ? "This session is still going. Your recap will be here when it ends."
                      : "This session was too short for a recap."));
            return (
                <Stack spacing={3} alignItems="center" sx={{ pt: 10, px: 3 }}>
                    <Typography variant="h5" textAlign="center">
                        {message}
                    </Typography>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={close}
                    >
                        Done
                    </Button>
                </Stack>
            );
        }
        if (!eligible) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 14 }}>
                    <CircularProgress color="secondary" />
                </Box>
            );
        }

        const previewSx = {
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            display: "block",
            borderRadius: 3,
            boxShadow: 8,
            opacity: imageLoading ? 0.5 : 1,
            transition: "opacity 150ms",
        } as const;
        return (
            <>
                {offerCustomPhoto && (
                    <input
                        ref={photoInput}
                        type="file"
                        accept="image/*"
                        hidden
                        data-testid="recap-photo-input"
                        onChange={(e) => void onPhotoPicked(e)}
                    />
                )}

                {/* The card takes whatever height is left and scales to fit,
                    so the controls below never fall off the screen. */}
                <Box
                    sx={{
                        flex: "1 1 auto",
                        minHeight: 160,
                        position: "relative",
                        containerType: "size",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Box
                            sx={{
                                ...cardFrameSx(),
                                ...(isCustom ? {} : TRANSPARENT_STAGE),
                            }}
                        >
                            {isCustom ? (
                                // Drawn here, not in an <img>, so a drag can
                                // move the photo without encoding every frame.
                                <Canvas
                                    ref={custom.canvasRef}
                                    width={card.width}
                                    height={card.height}
                                    role="img"
                                    aria-label="Your session recap card on your photo. Drag to move the photo."
                                    {...custom.dragHandlers}
                                    onPointerDown={(e) => {
                                        setDragHintSeen(true);
                                        custom.dragHandlers.onPointerDown(e);
                                    }}
                                    sx={{
                                        ...previewSx,
                                        touchAction: "none",
                                        cursor: "grab",
                                    }}
                                />
                            ) : (
                                image && (
                                    <Box
                                        component="img"
                                        src={image.objectUrl}
                                        alt="Your session recap card"
                                        sx={previewSx}
                                    />
                                )
                            )}
                            <Fade in={showDragHint} unmountOnExit>
                                <Typography variant="caption" sx={CARD_NOTE_SX}>
                                    Drag to move your photo
                                </Typography>
                            </Fade>
                        </Box>
                        {previewFailed && !previewLoading && (
                            <Stack
                                spacing={1}
                                alignItems="center"
                                justifyContent="center"
                                sx={{ position: "absolute", inset: 0, p: 2 }}
                            >
                                <Typography variant="body2" textAlign="center">
                                    {"We couldn't load your card."}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    onClick={
                                        imageFailed ? retryImage : pickPhoto
                                    }
                                >
                                    {imageFailed ? "Retry" : "Pick another"}
                                </Button>
                            </Stack>
                        )}
                        {previewLoading && (
                            <CircularProgress
                                color="secondary"
                                sx={{ position: "absolute" }}
                            />
                        )}
                    </Box>
                </Box>

                <Stack spacing={1.25} sx={{ flex: "none" }}>
                    {/* The only choice: what's behind the stats. */}
                    {offerCustomPhoto &&
                        (isCustom ? (
                            <Stack
                                direction="row"
                                justifyContent="center"
                                spacing={1}
                            >
                                <Button
                                    color="inherit"
                                    startIcon={<PhotoLibraryIcon />}
                                    disabled={busy || decodingPhoto}
                                    onClick={pickPhoto}
                                >
                                    Change photo
                                </Button>
                                <Button
                                    color="inherit"
                                    startIcon={<DeleteOutlineIcon />}
                                    disabled={busy || decodingPhoto}
                                    onClick={removePhoto}
                                >
                                    Remove photo
                                </Button>
                            </Stack>
                        ) : (
                            <Button
                                variant="outlined"
                                color="inherit"
                                size="large"
                                fullWidth
                                startIcon={<AddPhotoAlternateIcon />}
                                loading={decodingPhoto}
                                loadingPosition="start"
                                disabled={busy}
                                onClick={pickPhoto}
                            >
                                Add a photo
                            </Button>
                        ))}

                    <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        fullWidth
                        startIcon={<IosShareIcon />}
                        loading={busy}
                        loadingPosition="start"
                        // The card on screen is what gets shared, so wait for it
                        // to match the choices.
                        disabled={!shareReady}
                        onClick={() => void share()}
                    >
                        {needsTap ? "Tap to share" : "Share"}
                    </Button>

                    {saveInstead && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: "action.hover",
                            }}
                        >
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                Save your card, then post it from your photos.
                            </Typography>
                            <Button
                                variant="contained"
                                color="secondary"
                                disabled={!shareReady || !currentCard}
                                onClick={() =>
                                    currentCard &&
                                    downloadCard(currentCard, fileName)
                                }
                            >
                                Save card
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </>
        );
    })();

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <PageTitle parts={TITLE} />
            {/* One screen: the page is the viewport's height and only the
                card flexes, so Share is visible without scrolling. */}
            <Box
                sx={{
                    height: "100dvh",
                    overflowY: "auto",
                    background:
                        "linear-gradient(180deg, rgb(11, 23, 48) 0%, rgb(21, 39, 86) 100%)",
                    pt: "env(safe-area-inset-top, 0px)",
                    pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
                }}
            >
                <Stack
                    spacing={1.5}
                    sx={{
                        height: "100%",
                        minHeight: 520,
                        maxWidth: 520,
                        mx: "auto",
                        px: 2,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        sx={{ flex: "none", pt: 1 }}
                    >
                        <Stack sx={{ minWidth: 0 }}>
                            <Typography
                                component="h1"
                                variant="h4"
                                sx={{
                                    // As big as one line beside the close
                                    // button allows, from an SE up.
                                    fontSize: "clamp(1.375rem, 6.2vw, 1.75rem)",
                                    fontWeight: 900,
                                    lineHeight: 1.1,
                                    letterSpacing: "0.01em",
                                    textTransform: "uppercase",
                                }}
                            >
                                Share your session
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                {"Tag "}
                                <Box
                                    component="span"
                                    sx={{
                                        color: "secondary.main",
                                        fontWeight: 700,
                                    }}
                                >
                                    {VOLLEY_HANDLE}
                                </Box>
                                {" when you post"}
                            </Typography>
                        </Stack>
                        <IconButton
                            aria-label="Close"
                            onClick={close}
                            sx={{ mt: -0.5, mr: -1 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                    {content}
                </Stack>
            </Box>
            <Snackbar
                open={toast !== null}
                autoHideDuration={4000}
                onClose={() => setToast(null)}
                message={toast}
            />
        </ThemeProvider>
    );
}
